<template>
  <div>
    <jade-box
      :title="$t(title)"
      hasPadding
    >
      <div
        v-if="loading"
        class="eu-pa_6 eu-align_center"
        data-testid="show-loading"
      >
        <jade-spinner />
      </div>
      <div
        v-else
        class="flex flex-col gap-3"
      >
        <div
          v-for="(historyField, index) in parsedHistoryFields"
          :key="historyField.label"
        >
          <div class="flex w-full justify-between items-center gap-1 mb-3">
            <div class="flex flex-col">
              <jade-text
                color="medium"
                variant="text-small"
              >
                {{ historyField.label }}
              </jade-text>
              <jade-text
                v-if="historyField.isDateField"
                color="high"
                weight="semibold"
                variant="text-medium"
                :data-testid="historyField.testId"
              >
                {{ historyField.value | dateFormat('dateAndHour') }}
              </jade-text>
              <jade-text
                v-else-if="historyField.isLink"
                class="underline"
                color="high"
                weight="semibold"
                variant="text-medium"
                as="a"
                target="_blank"
                :href="$sanitize(historyField.href)"
                :data-testid="historyField.testId"
              >
                {{ historyField.value }}
              </jade-text>
              <jade-text
                v-else
                color="high"
                weight="semibold"
                variant="text-medium"
                :data-testid="historyField.testId"
              >
                {{ historyField.value }}
              </jade-text>
            </div>
            <jade-icon-button
              v-if="historyField.copyText"
              size="small"
              icon="action-copy"
              @click="copyToClipboard(historyField.copyText)"
            />
          </div>
          <jade-divider
            class="history-divider"
            v-if="index < parsedHistoryFields?.length - 1"
          />
        </div>
        <jade-alert
          v-if="technicianTrackingLink"
          variant="neutral"
          icon="circle-info"
          showIcon
        >
          <div class="flex flex-col gap-2">
            <jade-text
              color="high"
              variant="text-medium"
            >
              {{ $t('tracking-alert-label') }}
            </jade-text>
            <div class="flex items-center gap-1">
              <jade-text
                v-if="technicianTrackingLink"
                as="a"
                class="underline"
                weight="semibold"
                :href="$sanitize(technicianTrackingLink)"
                target="_blank"
                >{{ $t('tracking-alert-button') }}</jade-text
              >
              <jade-icon
                size="small"
                use="chevron-right"
              />
            </div>
          </div>
        </jade-alert>
      </div>
    </jade-box>
  </div>
</template>

<script>
import {
  JadeAlert,
  JadeBox,
  JadeDivider,
  JadeSpinner,
  JadeText,
  JadeIcon,
  JadeIconButton
} from '@jade'
import { EXTERNAL_ENDPOINTS } from '@/external-endpoints'

export default {
  name: 'HistoryDetails',
  props: {
    historyFields: Object,
    loading: Boolean,
    title: String
  },
  components: {
    JadeAlert,
    JadeBox,
    JadeDivider,
    JadeSpinner,
    JadeText,
    JadeIcon,
    JadeIconButton
  },
  computed: {
    hasTrackingField () {
      return (
        this.historyFields?.technicianName && this.historyFields?.trackingCode
      )
    },
    trackingField () {
      return [
        this.historyFields?.technicianName,
        this.historyFields?.trackingCode
      ]
        .filter(item => !!item)
        .join(' - ')
    },
    technicianTrackingLink () {
      const technicianName = this.historyFields?.technicianName?.toUpperCase()

      return EXTERNAL_ENDPOINTS.PHYSICAL_TRACKING[technicianName]?.url || ''
    },
    parsedHistoryFields () {
      const fieldMappings = [
        {
          key: 'activationCode',
          label: this.$t('activation-code'),
          testId: 'history-activationCode',
          value: this.historyFields.activationCode
        },
        {
          key: 'createdAt',
          label: this.$t('requested-in'),
          testId: 'history-createdAt',
          isDateField: true,
          value: this.historyFields.createdAt
        },
        {
          key: 'installedAt',
          label: this.$t('installed-in'),
          testId: 'history-installedAt',
          isDateField: true,
          value: this.historyFields.installedAt
        },
        {
          key: 'uninstalledAt',
          label: this.$t('uninstalled-on'),
          testId: 'history-uninstalledAt',
          isDateField: true,
          value: this.historyFields.uninstalledAt
        },
        {
          key: 'updatedAt',
          label: this.$t('updated'),
          testId: 'history-updatedAt',
          isDateField: true,
          value: this.historyFields.updatedAt
        },
        {
          key: 'reallocatedTo',
          label: this.$t('reallocated-to'),
          testId: 'history-reallocatedTo',
          value: this.historyFields.reallocatedTo,
          isLink: true,
          href: this.makeReallocatedLink(this.historyFields.reallocatedTo)
        },
        {
          key: 'reallocatedFrom',
          label: this.$t('reallocated-from'),
          testId: 'history-reallocatedFrom',
          value: this.historyFields.reallocatedFrom,
          isLink: true,
          href: this.makeReallocatedLink(this.historyFields.reallocatedFrom)
        },
        {
          key: 'trackingCode',
          label: this.$t('tracking-code'),
          value: this.hasTrackingField ? this.trackingField : '',
          copyText: this.historyFields.trackingCode
        }
      ]

      return fieldMappings.filter(item => item.value)
    }
  },
  methods: {
    makeReallocatedLink (reallocatedId) {
      const { href } = this.$router.resolve({
        name: 'serviceOrdersDetail',
        params: {
          id: reallocatedId
        }
      })
      return this.$sanitize(href)
    },
    copyToClipboard (value) {
      this.$copyText(value)
    }
  }
}
</script>

<i18n>
    {
      "pt-BR": {
        "general-data": "Dados gerais",
        "activation-code": "Código de ativação",
        "requested-in": "Solicitado em",
        "installed-in": "Instalado em",
        "uninstalled-on": "Desinstalado em",
        "updated": "Atualizado em",
        "service-order-history": "Histórico de ordem de serviço",
        "terminal-history": "Histórico de maquininha",
        "reallocated-from": "Realocado de",
        "reallocated-to": "Realocado para",
        "tracking-code": "Código de rastreio",
        "tracking-alert-label": "Você pode acompanhar o seu pedido através do código de rastreio",
        "tracking-alert-button": "Acessar"
      },
      "en-US": {
        "general-data": "General data",
        "activation-code": "Activation code",
        "requested-in": "Requested in",
        "installed-in": "Installed in",
        "uninstalled-on": "Uninstalled on",
        "service-order-history": "Service Order History",
        "terminal-history": "Terminal History",
        "reallocated-from": "Reallocated from",
        "reallocated-to": "Reallocated to",
        "tracking-code": "Tracking code",
        "tracking-alert-label": "You can track your order through the tracking code",
        "tracking-alert-button": "Access"
      }
    }
  </i18n>
